import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IPatientBaselineDetail } from "../../../../@types/admin/AdminBaseline";
import { TableSorted } from "../../../../components/TableSorted";
import { usePatientBaselines } from "../../../../hooks/queries/admin/baselines";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { humanizeEnumName } from "../../../../utils/enumNames";
import {
  ErrorMessage,
  FiltersContainer,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  SearchInput,
  StatusBadge,
} from "../AdminPage.styles";
import { TableContainer } from "../TableContainer";
import { TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { useMockPagination } from "../useMockPagination";

const SEARCH_FIELDS: (keyof IPatientBaselineDetail)[] = [
  "measurementType",
  "createdBy",
  "createdByName",
  "updatedBy",
  "updatedByName",
];

export interface IPatientBaselinesPanelProps {
  /** Patient whose baselines to show. Undefined keeps the query idle. */
  patientId: number | undefined;
  /**
   * Panel heading. Pass null to drop it — the Baselines page's modal already
   * names the patient in its own header, and a second heading under it read as
   * a duplicate.
   */
  title?: string | null;
  /** Rows per page. Defaults to the shared /admin page size. */
  pageSize?: number;
}

/**
 * "77 - Grace Hopper" when the audit value resolved to a user, the raw value on
 * its own when it did not.
 *
 * The raw value is not always an id: the ward-wide ISHLT recalculation writes
 * "Retool" and the workflow scheduler writes "WorkflowScheduler", so it is
 * shown as-is rather than dressed up as a person.
 */
export function describeAuditor(
  auditValue: string | null,
  name: string | null
): string {
  if (!auditValue) return "-";
  return name ? `${auditValue} - ${name}` : auditValue;
}

/**
 * Embeddable list of one patient's recorded baselines (EN-5168).
 *
 * A shared component rather than a page-local table because three Retool apps
 * show this same list: `page_Baselines`' full-screen modal, and the patient
 * details screens of `page_Patients` and `page_ArchivedPatients` (their
 * `lib/getBaselines.sql`, which is the same query). So EN-5169 / EN-5170 /
 * EN-5171 embed this instead of porting it again:
 *
 *   <PatientBaselinesPanel patientId={patient.id} />
 *
 * (`moduleBaselines` — the Retool global widget that *looks* like this
 * component's ancestor, complete with an unused `patientId` prop — is dead: it
 * is referenced by none of the 53 exports and its own queries still read the
 * page's table selection. It was an abandoned attempt at exactly this.)
 *
 * Archived baselines are listed, as Retool's table did, with a status column
 * saying which — a superseded baseline is part of the record.
 */
export function PatientBaselinesPanel({
  patientId,
  title = "Baselines",
  pageSize,
}: IPatientBaselinesPanelProps) {
  const { baselines, isLoading, isError } = usePatientBaselines(patientId);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: baselines,
    defaultPageSize: pageSize,
    searchFields: SEARCH_FIELDS,
  });

  const columns: ColumnDef<IPatientBaselineDetail, string>[] = useMemo(
    () => [
      {
        id: "measurementType",
        header: "Measurement",
        accessorFn: (baseline) => humanizeEnumName(baseline.measurementType),
        enableSorting: true,
      },
      {
        // Retool formatted this as a decimal column. The accessor renders the
        // number as text so a genuine 0 shows as "0" — TableSorted dashes a
        // falsy cell value and skips the renderer.
        id: "baseline",
        header: "Baseline value",
        accessorFn: (baseline) => String(baseline.baseline ?? ""),
        enableSorting: true,
      },
      {
        // Retool's valueOverride: item === true ? "ISHLT Baseline" : "Baseline".
        // The API sends the bool; the wording lives here.
        id: "isIshltBaseline",
        header: "Baseline type",
        accessorFn: (baseline) =>
          baseline.isIshltBaseline ? "ISHLT baseline" : "Baseline",
        enableSorting: true,
      },
      {
        // Retool's Active/Inactive tag, off a CASE in the SQL. The flag travels
        // as a bool and the label is built here.
        id: "status",
        header: "Status",
        accessorFn: (baseline) => (baseline.archived ? "Inactive" : "Active"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.archived ? "inactive" : "active"}>
            {row.original.archived ? "Inactive" : "Active"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "createdBy",
        header: "Created by",
        // Retool concatenated the id and the name into one string, which was
        // NULL in its entirety whenever the user lookup missed — losing the id
        // as well. The two fields travel separately; this joins them for display
        // and still shows the raw value on its own when there is no name.
        accessorFn: (baseline) =>
          describeAuditor(baseline.createdBy, baseline.createdByName),
        enableSorting: true,
      },
      {
        accessorKey: "updated",
        header: "Updated",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.updated, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "updatedBy",
        header: "Updated by",
        accessorFn: (baseline) =>
          describeAuditor(baseline.updatedBy, baseline.updatedByName),
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PanelContainer>
      {title && (
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
        </PanelHeader>
      )}

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by measurement or who recorded it..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load this patient&apos;s baselines. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-patient-baselines"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "No baselines recorded"
            }
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "patient-baselines",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PanelContainer>
  );
}
